import type { ApiProblem, CsrfTokenResponse, QueryParams } from './types'
import { createUuid } from '@/utils/uuid'
import { assertPortalMutationOnline } from '@/portal/portalOffline'
import { assertPortalPreviewRequest, isPortalPreviewActive, portalPreviewApiPath } from '@/portal/portalPreview'

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '')

export class ApiError extends Error {
  readonly status: number
  readonly problem: ApiProblem

  constructor(status: number, problem: ApiProblem) {
    super(problem.detail || problem.title || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  query?: QueryParams
  body?: unknown
  onUploadProgress?: (percent: number) => void
}

const isMutating = (method?: string) => !['GET', 'HEAD', 'OPTIONS'].includes((method || 'GET').toUpperCase())

let csrfToken: CsrfTokenResponse | null = null
let csrfRequest: Promise<CsrfTokenResponse> | null = null
let csrfGeneration = 0
let apiSessionGeneration = 0
let apiSessionController = new AbortController()

export class StaleSessionError extends Error {
  constructor() {
    super('API response belongs to an expired browser session')
    this.name = 'StaleSessionError'
  }
}

function assertCurrentSession(generation: number): void {
  if (generation !== apiSessionGeneration) throw new StaleSessionError()
}

/**
 * Invalidates every request started for the previous authenticated browser
 * session. The generation check is deliberate: even a test double or browser
 * transport that ignores AbortSignal cannot publish a late response.
 */
export function advanceApiSessionBoundary(): void {
  apiSessionGeneration += 1
  apiSessionController.abort(new StaleSessionError())
  apiSessionController = new AbortController()
  clearCsrfToken()
}

function requestSignal(external: AbortSignal | null | undefined, boundary: AbortSignal): {
  signal: AbortSignal
  cleanup: () => void
} {
  if (!external) return { signal: boundary, cleanup: () => undefined }

  const controller = new AbortController()
  const abortFromBoundary = () => controller.abort(boundary.reason)
  const abortFromExternal = () => controller.abort(external.reason)
  if (boundary.aborted) abortFromBoundary()
  else boundary.addEventListener('abort', abortFromBoundary, { once: true })
  if (external.aborted) abortFromExternal()
  else external.addEventListener('abort', abortFromExternal, { once: true })

  return {
    signal: controller.signal,
    cleanup: () => {
      boundary.removeEventListener('abort', abortFromBoundary)
      external.removeEventListener('abort', abortFromExternal)
    },
  }
}

export function buildApiUrl(path: string, query?: QueryParams): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${API_BASE_URL}${portalPreviewApiPath(normalizedPath)}`, window.location.origin)

  Object.entries(query || {}).forEach(([key, raw]) => {
    if (raw === null || raw === undefined || raw === '') return
    const values = Array.isArray(raw) ? raw : [raw]
    values.forEach(value => {
      url.searchParams.append(key, value instanceof Date ? value.toISOString() : String(value))
    })
  })

  return `${url.pathname}${url.search}`
}

async function problemFrom(response: Response): Promise<ApiProblem> {
  const fallback: ApiProblem = {
    status: response.status,
    title: response.statusText || 'Ошибка запроса',
  }

  try {
    const json = await response.json() as Partial<ApiProblem>
    return {
      ...fallback,
      ...json,
      status: response.status,
    }
  } catch {
    return fallback
  }
}

export function clearCsrfToken(): void {
  csrfGeneration += 1
  csrfToken = null
  csrfRequest = null
}

export async function ensureCsrfToken(force = false): Promise<CsrfTokenResponse> {
  if (force) clearCsrfToken()
  if (csrfToken) return csrfToken
  if (csrfRequest) return csrfRequest

  const sessionGeneration = apiSessionGeneration
  const tokenGeneration = csrfGeneration
  const request = fetch(buildApiUrl('/auth/csrf'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    signal: apiSessionController.signal,
  }).then(async response => {
    if (!response.ok) throw new ApiError(response.status, await problemFrom(response))
    const value = await response.json() as CsrfTokenResponse
    assertCurrentSession(sessionGeneration)
    if (tokenGeneration !== csrfGeneration) throw new StaleSessionError()
    csrfToken = value
    return value
  })
  csrfRequest = request
  try {
    return await request
  } finally {
    if (csrfRequest === request) csrfRequest = null
  }
}

/** XHR is used only for file uploads: fetch has no browser upload progress events. */
function uploadWithProgress(url: string, method: string, headers: Headers, body: FormData, signal: AbortSignal, progress: (percent: number) => void): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const abort = () => xhr.abort()
    const cleanup = () => signal.removeEventListener('abort', abort)
    xhr.open(method, url)
    xhr.withCredentials = true
    xhr.responseType = 'blob'
    headers.forEach((value, name) => xhr.setRequestHeader(name, value))
    xhr.upload.onprogress = event => { if (event.lengthComputable && !signal.aborted) progress(Math.min(100, Math.floor(event.loaded / event.total * 100))) }
    xhr.upload.onload = () => { if (!signal.aborted) progress(100) }
    xhr.onload = () => {
      cleanup()
      if (signal.aborted) { reject(signal.reason ?? new DOMException('Aborted', 'AbortError')); return }
      const responseHeaders = new Headers()
      for (const line of xhr.getAllResponseHeaders().trim().split(/[\r\n]+/)) {
        const separator = line.indexOf(':')
        if (separator > 0) responseHeaders.append(line.slice(0, separator).trim(), line.slice(separator + 1).trim())
      }
      resolve(new Response(xhr.status === 204 ? null : xhr.response, { status: xhr.status, statusText: xhr.statusText, headers: responseHeaders }))
    }
    xhr.onerror = () => { cleanup(); reject(new TypeError('Network request failed')) }
    xhr.onabort = () => { cleanup(); reject(signal.reason ?? new DOMException('Aborted', 'AbortError')) }
    if (signal.aborted) { reject(signal.reason ?? new DOMException('Aborted', 'AbortError')); return }
    signal.addEventListener('abort', abort, { once: true })
    xhr.send(body)
  })
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
  retryCsrf = true,
): Promise<T> {
  const sessionGeneration = apiSessionGeneration
  const boundary = apiSessionController.signal
  const boundSignal = requestSignal(options.signal, boundary)
  const method = (options.method || 'GET').toUpperCase()
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')

  try {
    assertPortalPreviewRequest(path, method)
    if (path.startsWith('/portal/') && isMutating(method)) assertPortalMutationOnline()
    if (isMutating(method)) {
      const csrf = await ensureCsrfToken()
      assertCurrentSession(sessionGeneration)
      headers.set(csrf.headerName, csrf.token)
    }

    let body: BodyInit | undefined
    if (options.body instanceof FormData) {
      // The browser must add the multipart boundary itself. Keeping a caller
      // supplied JSON content type here would produce an unreadable upload.
      headers.delete('Content-Type')
      body = options.body
    } else if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(options.body)
    }

    if (isMutating(method) && !headers.has('X-Request-Id')) {
      headers.set('X-Request-Id', createUuid())
    }

    const response = options.onUploadProgress && body instanceof FormData
      ? await uploadWithProgress(buildApiUrl(path, options.query), method, headers, body, boundSignal.signal, percent => {
          if (sessionGeneration === apiSessionGeneration) options.onUploadProgress?.(percent)
        })
      : await fetch(buildApiUrl(path, options.query), {
      ...options,
      method,
      headers,
      body,
      credentials: 'include',
      ...(isPortalPreviewActive() ? { cache: 'no-store' as const } : {}),
      signal: boundSignal.signal,
    })
    assertCurrentSession(sessionGeneration)

    if (!response.ok) {
      const problem = await problemFrom(response)
      assertCurrentSession(sessionGeneration)
      if (isPortalPreviewActive() && ['portal.preview_invalid', 'portal.preview_expired', 'portal.preview_forbidden'].includes(problem.code ?? '') && !path.startsWith('/portal-previews/')) {
        window.dispatchEvent(new CustomEvent('avoschool:auth-required'))
      }
      if (response.status === 403 && isMutating(method) && retryCsrf) {
        await ensureCsrfToken(true)
        assertCurrentSession(sessionGeneration)
        return apiRequest<T>(path, options, false)
      }
      if (response.status === 401 && path !== '/auth/login' && path !== '/auth/me' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('avoschool:auth-required'))
      }
      throw new ApiError(response.status, problem)
    }

    if (response.status === 204) return undefined as T

    const contentType = response.headers.get('content-type') || ''
    const value = contentType.includes('json')
      ? await response.json() as T
      : await response.text() as T
    assertCurrentSession(sessionGeneration)
    return value
  } finally {
    boundSignal.cleanup()
  }
}

export interface ApiFileDownload {
  blob: Blob
  filename: string | null
}

export async function apiFileDownload(
  path: string,
  query?: QueryParams,
  signal?: AbortSignal,
  options?: { accept?: string; cache?: RequestCache },
): Promise<ApiFileDownload> {
  const sessionGeneration = apiSessionGeneration
  const boundSignal = requestSignal(signal, apiSessionController.signal)
  try {
    assertPortalPreviewRequest(path)
    const response = await fetch(buildApiUrl(path, query), {
      method: 'GET',
      headers: { Accept: options?.accept ?? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      cache: isPortalPreviewActive() ? 'no-store' : options?.cache,
      credentials: 'include',
      signal: boundSignal.signal,
    })
    assertCurrentSession(sessionGeneration)
    if (!response.ok) {
      if (response.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('avoschool:auth-required'))
      }
      throw new ApiError(response.status, await problemFrom(response))
    }

    const disposition = response.headers.get('content-disposition') ?? ''
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1]
    const plain = /filename="?([^";]+)"?/i.exec(disposition)?.[1]
    const filename = encoded ? decodeURIComponent(encoded) : plain ?? null
    const blob = await response.blob()
    assertCurrentSession(sessionGeneration)
    return { blob, filename }
  } finally {
    boundSignal.cleanup()
  }
}

export function saveApiFile(download: ApiFileDownload, fallbackFilename: string): void {
  const url = URL.createObjectURL(download.blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = download.filename || fallbackFilename
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

export const api = {
  get: <T>(path: string, query?: QueryParams, signal?: AbortSignal) =>
    apiRequest<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown, query?: QueryParams) =>
    apiRequest<T>(path, { method: 'POST', body, query }),
  postForm: <T>(path: string, body: FormData, query?: QueryParams, onUploadProgress?: (percent: number) => void) =>
    apiRequest<T>(path, { method: 'POST', body, query, onUploadProgress }),
  put: <T>(path: string, body?: unknown, query?: QueryParams) =>
    apiRequest<T>(path, { method: 'PUT', body, query }),
  patch: <T>(path: string, body?: unknown, query?: QueryParams) =>
    apiRequest<T>(path, { method: 'PATCH', body, query }),
  delete: <T = void>(path: string, query?: QueryParams) =>
    apiRequest<T>(path, { method: 'DELETE', query }),
}

export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = error.problem.errors?.map(item => `${item.field}: ${item.message}`).join('; ')
    return fields || error.message
  }
  if (error instanceof Error) return error.message
  return 'Неизвестная ошибка'
}
